import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, CalendarClock, Sun, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { dubaiToday, fmtDateShort, fmtTime } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/admin/StatusBadge";
import { RegistrationSheet } from "@/components/admin/RegistrationSheet";

const CARDS = [
  ["total_registrations", "Total registrations", Users],
  ["upcoming", "Upcoming appointments", CalendarClock],
  ["today", "Today’s appointments", Sun],
  ["completed", "Completed", CheckCircle2],
  ["cancelled", "Cancelled", XCircle],
];

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [next, setNext] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/registrations", { params: { date_from: dubaiToday(), sort: "start_utc", order: "asc", page_size: 20 } })
      .then((r) => setNext(r.data.items.filter((i) => ["confirmed", "rescheduled"].includes(i.status) && new Date(i.start_utc) > new Date()).slice(0, 6)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-overview">
      <PageHeader title="Overview" subtitle="Live figures from your booking database." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {CARDS.map(([k, label, Icon]) => (
          <div key={k} className="glass rounded-3xl p-5" data-testid={`stat-card-${k}`}>
            <Icon size={18} className="text-[#C7A76C]" strokeWidth={1.75} />
            <p className="mt-6 font-display text-3xl font-bold text-[#0B1F3A]" data-testid={`stat-value-${k}`}>{stats ? stats[k] : "–"}</p>
            <p className="mt-1 text-xs text-[#697386]">{label}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 glass rounded-3xl p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-[#172033]">Next appointments</h2>
          <Link to="/admin/registrations" className="text-sm text-[#697386] hover:text-[#0B1F3A] flex items-center gap-1" data-testid="overview-view-all-link">View all <ArrowRight size={14} /></Link>
        </div>
        <ul className="mt-4 divide-y divide-[rgba(20,35,55,0.08)]" data-testid="overview-next-list">
          {next?.length === 0 && <li className="py-6 text-sm text-[#697386]">No upcoming appointments yet. New bookings will appear here.</li>}
          {next?.map((a) => (
            <li key={a.id}>
              <button onClick={() => setOpenId(a.id)} className="w-full py-4 flex items-center justify-between gap-4 text-left hover:bg-black/[0.02] rounded-xl px-2" data-testid={`overview-appt-${a.id}`}>
                <div>
                  <p className="font-medium text-[#172033]">{a.full_name}</p>
                  <p className="text-xs text-[#697386]">{a.country}</p>
                </div>
                <div className="text-right flex items-center gap-4">
                  <div>
                    <p className="text-sm font-medium">{fmtDateShort(a.start_utc)}</p>
                    <p className="text-xs text-[#697386]">{fmtTime(a.start_utc)} GST</p>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <RegistrationSheet id={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
